import { FC } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';

import {
  ProgramActivitiesQuery,
  ProgramActivityDataFragment,
} from '../../../generated/graphql';
import { useAuth } from '../../../contexts/AuthContext';
import { ColumnHeaderType } from '../../types/tables';

import TableHeader from '../TableHeader';
import ActivityRow from './ActivityRow';

const columns: ColumnHeaderType[] = [
  {
    field: 'name',
    headerName: 'Name',
    isSortable: false,
  },
  {
    field: 'type',
    headerName: 'Category',
    isSortable: false,
  },
  {
    field: 'status',
    headerName: 'Status',
    isSortable: false,
  },
  {
    field: 'sent',
    headerName: 'Sent',
    isSortable: false,
  },
  {
    field: 'completed',
    headerName: 'Client completed',
    headerNameAlt: 'Completed',
    isSortable: false,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    isSortable: false,
  },
];

export type ProgramActivities = ProgramActivitiesQuery['programActivities'];

const ActivityTable: FC<{
  programActivities: ProgramActivities | undefined;
  programActivitiesLoading: boolean;
  onClickProgramActivity: (
    programActivity: ProgramActivityDataFragment,
  ) => void;
  patientResourcesMode?: boolean;
  patientName?: string;
  programId?: string;
  onExpandResponses?: (programActivity: ProgramActivityDataFragment) => void;
  onTextOpen?: (
    activityText: ProgramActivityDataFragment['activityText'],
  ) => void;
  onMarkComplete?: (programActivity: ProgramActivityDataFragment) => void;
  isCompletingProgramActivity?: boolean;
}> = ({
  programActivities,
  programActivitiesLoading,
  onClickProgramActivity,
  patientResourcesMode = false,
  patientName,
  programId,
  onExpandResponses,
  onTextOpen,
  onMarkComplete,
  isCompletingProgramActivity = false,
}) => {
  const navigate = useNavigate();
  const { authedProviderUser } = useAuth();

  const showProgramActivities =
    !programActivitiesLoading &&
    programActivities &&
    programActivities?.length > 0;

  return (
    <div className="max-h-[calc(100vh-224px)] overflow-y-scroll pb-4">
      <table
        className={classNames(
          'min-w-full transition-all duration-150',
          !showProgramActivities && 'opacity-0',
          showProgramActivities && 'opacity-100',
        )}
      >
        <thead className="sticky top-0 z-10 h-[40px] w-full">
          <tr className="bg-white">
            {columns.map((column, i) => {
              const isLastColumn = i === columns.length - 1;

              return (
                <TableHeader
                  key={`activityTableColumnHeader_${column.field}`}
                  column={column}
                  isLastColumn={isLastColumn}
                  useAltHeaderName={patientResourcesMode}
                />
              );
            })}
          </tr>
        </thead>
        <tbody>
          {programActivities?.map((programActivity, index) => {
            return (
              <ActivityRow
                key={`activityTableRow_${index}`}
                programActivity={programActivity}
                onClickProgramActivity={onClickProgramActivity}
                patientResourcesMode={patientResourcesMode}
                authedProviderUserId={authedProviderUser?.id}
                patientName={patientName}
                programId={programId}
                navigate={navigate}
                onExpandResponses={onExpandResponses}
                onTextOpen={onTextOpen}
                onMarkComplete={onMarkComplete}
                isCompletingProgramActivity={isCompletingProgramActivity}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ActivityTable;
